import {
    backdropClasses,
    filledInputClasses,
    outlinedInputClasses,
    paperClasses,
    tableCellClasses
} from '@mui/material'
import { common } from '@mui/material/colors'
import { alpha } from '@mui/material/styles'

export const createComponents = ({ palette }) => ({
    MuiAccordion: {
        styleOverrides: {
            root: {
                border: '1px solid',
                borderColor: alpha(palette.primary.main, 0.12),
                backgroundColor: alpha(common.white, 0.72),
                '&:before': {
                    display: 'none'
                }
            }
        }
    },
    MuiAvatar: {
        styleOverrides: {
            root: {
                backgroundColor: alpha(palette.primary.main, 0.12),
                color: palette.primary.dark
            }
        }
    },
    MuiBackdrop: {
        styleOverrides: {
            root: {
                [`&:not(.${backdropClasses.invisible})`]: {
                    backgroundColor: alpha(palette.neutral[900], 0.6)
                }
            }
        }
    },
    MuiCard: {
        styleOverrides: {
            root: {
                border: `1px solid ${alpha(palette.primary.main, 0.12)}`,
                backgroundColor: alpha(common.white, 0.86),
                transition: 'box-shadow 180ms ease, border-color 180ms ease',
                [`&.${paperClasses.elevation1}`]: {
                    boxShadow:
                        '0px 12px 24px rgba(17, 25, 39, 0.05), 0px 1px 2px rgba(17, 25, 39, 0.03)'
                },
                '&:hover': {
                    borderColor: alpha(palette.primary.main, 0.2),
                    boxShadow:
                        '0px 16px 30px rgba(17, 25, 39, 0.07), 0px 3px 8px rgba(17, 25, 39, 0.04)'
                }
            }
        }
    },
    MuiButton: {
        styleOverrides: {
            root: {
                fontWeight: 600,
                letterSpacing: '-0.01em'
            },
            contained: {
                boxShadow: `0px 8px 18px ${alpha(palette.primary.main, 0.2)}`,
                '&:hover': {
                    boxShadow: `0px 12px 24px ${alpha(palette.primary.main, 0.24)}`
                }
            },
            outlined: {
                borderColor: alpha(palette.primary.main, 0.18),
                backgroundColor: alpha(common.white, 0.55),
                '&:hover': {
                    borderColor: alpha(palette.primary.main, 0.28),
                    backgroundColor: alpha(palette.primary.main, 0.05)
                }
            },
            text: {
                '&:hover': {
                    backgroundColor: alpha(palette.primary.main, 0.06)
                }
            }
        }
    },
    MuiChip: {
        styleOverrides: {
            icon: {
                color: palette.action.active
            },
            root: {
                borderColor: alpha(palette.primary.main, 0.12),
                backgroundColor: alpha(common.white, 0.82)
            }
        }
    },
    MuiCssBaseline: {
        styleOverrides: {
            body: {
                background:
                    `radial-gradient(circle at 0% 0%, ${alpha(
                        palette.primary.main,
                        0.08
                    )} 0%, transparent 45%), ` + palette.background.default
            },
            '#nprogress .bar': {
                backgroundColor: palette.primary.main
            },
            '.slick-dots li button': {
                '&:before': {
                    fontSize: 10,
                    color: palette.primary.main
                }
            },
            '.slick-dots li.slick-active button': {
                '&:before': {
                    color: palette.primary.main
                }
            }
        }
    },
    MuiFormLabel: {
        styleOverrides: {
            root: {
                backgroundColor: palette.background.paper,
                paddingRight: '4px',
                paddingLeft: '4px',
                color: palette.text.secondary,
                borderRadius: 4
            }
        }
    },
    MuiInputBase: {
        styleOverrides: {
            input: {
                '&::placeholder': {
                    color: palette.text.secondary
                }
            }
        }
    },
    MuiFilledInput: {
        styleOverrides: {
            root: {
                backgroundColor: alpha(common.white, 0.74),
                borderColor: alpha(palette.primary.main, 0.14),
                '&:hover': {
                    backgroundColor: alpha(common.white, 0.92),
                    borderColor: alpha(palette.primary.main, 0.2)
                },
                [`&.${filledInputClasses.disabled}`]: {
                    backgroundColor: 'transparent'
                },
                [`&.${filledInputClasses.focused}`]: {
                    backgroundColor: common.white,
                    borderColor: palette.primary.main,
                    boxShadow: `0 0 0 4px ${alpha(palette.primary.main, 0.12)}`
                },
                [`&.${filledInputClasses.error}`]: {
                    borderColor: palette.error.main,
                    boxShadow: `0 0 0 4px ${alpha(palette.error.main, 0.12)}`
                }
            }
        }
    },
    MuiPaper: {
        styleOverrides: {
            root: {
                border: `1px solid ${alpha(palette.primary.main, 0.08)}`
            }
        }
    },
    MuiOutlinedInput: {
        styleOverrides: {
            root: {
                backgroundColor: alpha(common.white, 0.62),
                '&:hover': {
                    backgroundColor: alpha(common.white, 0.9),
                    [`& .${outlinedInputClasses.notchedOutline}`]: {
                        borderColor: alpha(palette.primary.main, 0.18)
                    }
                },
                [`&.${outlinedInputClasses.focused}`]: {
                    backgroundColor: common.white,
                    [`& .${outlinedInputClasses.notchedOutline}`]: {
                        borderColor: palette.primary.main,
                        boxShadow: `0 0 0 4px ${alpha(palette.primary.main, 0.12)}`
                    }
                },
                [`&.${outlinedInputClasses.error}`]: {
                    [`& .${outlinedInputClasses.notchedOutline}`]: {
                        borderColor: palette.error.main,
                        boxShadow: `0 0 0 4px ${alpha(palette.error.main, 0.12)}`
                    }
                }
            },
            notchedOutline: {
                borderColor: alpha(palette.primary.main, 0.14)
            }
        }
    },

    MuiSwitch: {
        styleOverrides: {
            switchBase: {
                color: palette.neutral[500]
            },
            track: {
                backgroundColor: palette.neutral[400],
                opacity: 1
            }
        }
    },
    MuiTableCell: {
        styleOverrides: {
            root: {
                borderBottomColor: palette.divider
            }
        }
    },
    MuiTableHead: {
        styleOverrides: {
            root: {
                [`& .${tableCellClasses.root}`]: {
                    backgroundColor: alpha(palette.primary.main, 0.05),
                    color: palette.neutral[700],
                    fontWeight: 600
                }
            }
        }
    },
    MuiTimelineConnector: {
        styleOverrides: {
            root: {
                backgroundColor: palette.divider
            }
        }
    },
    MuiTooltip: {
        styleOverrides: {
            tooltip: {
                backdropFilter: 'blur(6px)',
                background: alpha(palette.neutral[900], 0.82),
                border: `1px solid ${alpha(common.white, 0.08)}`
            }
        }
    }
})
